import { TextSelection } from 'prosemirror-state';
import { navigationStore } from '../stores/navigationStore';
import { getSectionDisplayName } from '../components/editors/spec/blocks/blockDefinitions';

/**
 * Service for extracting and managing document structure from ProseMirror
 */
class DocumentStructureService {
    constructor() {
        this.updateTimer = null;
        this.lastStructureHash = null;
    }

    /**
     * Extract hierarchical document structure from ProseMirror doc
     * @param {Object} doc - ProseMirror document
     * @returns {Array} Hierarchical structure array
     */
    extractDocumentStructure(doc) {
        const structure = [];
        let currentH1 = null;
        let currentH2 = null;
        let currentH3 = null;
        let idCounter = 0;

        doc.descendants((node, pos) => {
            if (node.type.name === 'heading') {
                const level = node.attrs.level;
                const text = node.textContent.trim();
                
                // Skip empty headings
                if (!text) return;

                const item = {
                    id: `heading-${idCounter++}`,
                    level,
                    text,
                    position: pos,
                    sectionType: node.attrs.sectionType || null,
                    children: []
                };

                // Build hierarchical structure
                switch (level) {
                    case 1:
                        currentH1 = item;
                        currentH2 = null;
                        currentH3 = null;
                        structure.push(item);
                        break;
                    
                    case 2:
                        currentH2 = item;
                        currentH3 = null;
                        if (currentH1) {
                            currentH1.children.push(item);
                        } else {
                            structure.push(item);
                        }
                        break;
                    
                    case 3:
                        currentH3 = item;
                        if (currentH2) {
                            currentH2.children.push(item);
                        } else if (currentH1) {
                            currentH1.children.push(item);
                        } else {
                            structure.push(item);
                        }
                        break;
                    
                    case 4:
                        if (currentH3) {
                            currentH3.children.push(item);
                        } else if (currentH2) {
                            currentH2.children.push(item);
                        } else if (currentH1) {
                            currentH1.children.push(item);
                        } else {
                            structure.push(item);
                        }
                        break;
                }
            }
        });

        return structure;
    }

    /**
     * Navigate to a specific position in the editor
     * @param {Object} editor - TipTap editor instance
     * @param {number} position - Target position in document
     */
    navigateToPosition(editor, position) {
        if (!editor || typeof position !== 'number') return;

        try {
            const { state } = editor;
            const { doc } = state;

            // Validate position
            if (position < 0 || position > doc.content.size) {
                console.warn('Invalid navigation position:', position);
                return;
            }

            // Create transaction to update selection and scroll
            const tr = state.tr;
            
            // Set selection at the start of the heading
            const selection = TextSelection.create(doc, position);
            tr.setSelection(selection);
            
            // Mark the transaction to scroll into view
            tr.scrollIntoView();
            
            // Dispatch the transaction
            editor.view.dispatch(tr);

            // Additional smooth scrolling for better UX
            setTimeout(() => {
                try {
                    const domAtPos = editor.view.domAtPos(position);
                    if (domAtPos && domAtPos.node) {
                        const element = domAtPos.node.nodeType === Node.TEXT_NODE 
                            ? domAtPos.node.parentElement 
                            : domAtPos.node;
                        
                        if (element) {
                            element.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest'
                            });
                        }
                    }
                } catch (e) {
                    // Fallback if DOM position fails
                    const coords = editor.view.coordsAtPos(position);
                    if (coords) {
                        window.scrollTo({
                            top: coords.top - window.innerHeight / 2,
                            behavior: 'smooth'
                        });
                    }
                }
            }, 50);

            // Focus the editor
            editor.view.focus();
        } catch (error) {
            console.error('Navigation error:', error);
        }
    }

    /**
     * Update navigation structure with debouncing
     * @param {Object} doc - ProseMirror document
     */
    updateStructure(doc) {
        // Clear existing timer
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
        }

        // Debounce updates
        this.updateTimer = setTimeout(() => {
            const structure = this.extractDocumentStructure(doc);
            
            // Only update if structure has changed
            const structureHash = JSON.stringify(structure);
            if (structureHash !== this.lastStructureHash) {
                this.lastStructureHash = structureHash;
                navigationStore.updateStructure(structure);
            }
        }, 300);
    }

    /**
     * Find current heading based on cursor position
     * @param {Object} doc - ProseMirror document
     * @param {number} cursorPos - Current cursor position
     * @returns {string|null} ID of current heading
     */
    findCurrentHeading(doc, cursorPos) {
        let currentHeadingId = null;
        let lastHeadingId = null;
        let idCounter = 0;

        doc.descendants((node, pos) => {
            if (node.type.name === 'heading') {
                const headingId = `heading-${idCounter++}`;
                
                if (pos <= cursorPos) {
                    lastHeadingId = headingId;
                }
                
                // If cursor is within the heading node
                if (pos <= cursorPos && pos + node.nodeSize > cursorPos) {
                    currentHeadingId = headingId;
                    return false; // Stop iteration
                }
            }
        });

        return currentHeadingId || lastHeadingId;
    }

    /**
     * Get section display name
     * @param {string} sectionType - Section type identifier
     * @returns {string} Display name
     */
    getSectionDisplayName(sectionType) {
        return getSectionDisplayName(sectionType);
    }

    /**
     * Filter structure based on search query
     * @param {Array} structure - Document structure
     * @param {string} query - Search query
     * @returns {Array} Filtered structure
     */
    filterStructure(structure, query) {
        if (!query) return structure;

        const lowerQuery = query.toLowerCase();
        
        const filterItems = (items) => {
            return items.reduce((filtered, item) => {
                const matchesQuery = item.text.toLowerCase().includes(lowerQuery);
                const filteredChildren = item.children ? filterItems(item.children) : [];
                
                // Include item if it matches or has matching children
                if (matchesQuery || filteredChildren.length > 0) {
                    filtered.push({
                        ...item,
                        children: filteredChildren
                    });
                }
                
                return filtered;
            }, []);
        };

        return filterItems(structure);
    }


    /**
     * Clean up resources
     */
    destroy() {
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
            this.updateTimer = null;
        }
        this.lastStructureHash = null;
    }
}

// Export singleton instance
export const documentStructureService = new DocumentStructureService();